import { Stack, Tab, Tabs } from '@mui/material';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import { uniq } from 'lodash';
import { useMemo, useState } from 'react';

import { LoadingIndicator } from '../LoadingIndicator';
import {
  ROLE_PERMISSION_COLUMN_WIDTHS,
  ROLE_PERMISSION_MATRIX_ABBREVIATIONS,
  SCOPE_FEATURES
} from './RolePermissions.constants';
import useFetchRolePermissions from './RolePermissions.hooks';
import type { Role, ScopeFeatureKey } from './RolePermissions.types';

const mapPermissionToAction = (permission: string) => {
  const [, action] = permission.split('.');
  return ROLE_PERMISSION_MATRIX_ABBREVIATIONS[
    action as keyof typeof ROLE_PERMISSION_MATRIX_ABBREVIATIONS
  ];
};

const sortActions = (actions: string[]): string[] => {
  const actionOrder = {
    R: 1,
    W: 2,
    D: 3
  };

  return actions.sort((a, b) => {
    const aOrder = actionOrder[a as keyof typeof actionOrder] || 0;
    const bOrder = actionOrder[b as keyof typeof actionOrder] || 0;
    return aOrder - bOrder;
  });
};

export const RolePermissionsGlobalMatrix = () => {
  const { data: roles, isFetching, isPending } = useFetchRolePermissions();
  const [activeTab, setActiveTab] = useState<'global' | 'organization'>(
    'global'
  );

  const handleTabChange = (
    _: React.SyntheticEvent,
    newValue: 'global' | 'organization'
  ) => {
    setActiveTab(newValue);
  };

  const filteredRoles =
    roles?.filter((role: Role) => role.type === activeTab) || [];

  const rows = useMemo(() => {
    const tableRows: Record<string, string>[] = [];

    Object.keys(SCOPE_FEATURES).forEach((key) => {
      const row: Record<string, string> = {
        id: key,
        name: SCOPE_FEATURES[key as ScopeFeatureKey]
      };

      filteredRoles.forEach((role: Role) => {
        const rolePermissions = role.permissions.filter((permission) =>
          permission.startsWith(key)
        );
        const action = rolePermissions.map((permission) =>
          mapPermissionToAction(permission)
        );

        row[role.name] = sortActions(uniq(action)).join('');
      });
      tableRows.push(row);
    });
    return tableRows;
  }, [filteredRoles]);

  const columns = useMemo(() => {
    const cols: GridColDef[] = [
      {
        field: 'name',
        headerName: '',
        width: 200,
        renderCell: ({ value }) => <strong>{value}</strong>
      }
    ];

    filteredRoles.forEach((role: Role) => {
      cols.push({
        field: role.name,
        headerName: role.name.toUpperCase(),
        align: 'center',
        headerAlign: 'center',
        width:
          ROLE_PERMISSION_COLUMN_WIDTHS[
            role.name as keyof typeof ROLE_PERMISSION_COLUMN_WIDTHS
          ] || 90
      });
    });

    return cols;
  }, [filteredRoles]);

  if (isPending || isFetching) {
    return <LoadingIndicator isLoading={isFetching} />;
  }

  return (
    <Stack sx={{ height: '100%' }}>
      {/* Tabs Section */}
      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        textColor="primary"
        indicatorColor="primary"
        sx={{ mb: 3 }}
      >
        <Tab label="Global Roles" value="global" />
        <Tab label="Organization Roles" value="organization" />
      </Tabs>

      {/* Roles and Permissions Table */}
      <DataGrid
        hideFooter
        rows={rows}
        columns={columns}
        density="compact"
        disableColumnFilter
        disableColumnMenu
        rowSelection={false}
        sx={{ bgcolor: 'var(--mui-palette-background-elevation2)' }}
      />
    </Stack>
  );
};

export default RolePermissionsGlobalMatrix;
